import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';

import SectionTitleBar from '../../../components/titlebar';
import Loading from '../../../Animations/loadScreen';
import { ProfileQL } from '../../../../models/Profile';
import { BaseReact } from '../../../../base.model';
import { graphqlSchema } from '../../../../services/graphql.schema';
import { StripeCheckoutSessionResponse } from '../../../../models/Stripe';
import { BillingAccount } from '../../../../models/BillingAccount';
import { useCurrentUser } from '../../../../contexts/CurrentUserContext';
import './CheckoutSuccess.scss';

interface CheckoutSuccessProps extends BaseReact {
    currentUser: ProfileQL;
}

let retryTimer: NodeJS.Timeout | null = null;

function CheckoutSuccess({
    currentUser
}: CheckoutSuccessProps) {
    const { recallCurrentUser } = useCurrentUser();
    const query = new URLSearchParams(window.location.search);
    const sessionId = query.get('session_id');

    const [isLoadingSession, setIsLoadingSession] = useState(true);
    const [sessionData, setSessionData] = useState<StripeCheckoutSessionResponse | null>(null);

    const [isLoadingBilling, setIsLoadingBilling] = useState(true);
    const [attempts, setAttempts] = useState(0);
    const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);

    const [hasError, setHasError] = useState(false);

    useQuery(graphqlSchema.STRIPE.PRICES.getCheckoutSession, {
        variables: { sessionId: sessionId || '' },
        onCompleted: ({ getCheckoutSession }: { getCheckoutSession: StripeCheckoutSessionResponse }) => {
            console.log('Checkout Session Data: ', getCheckoutSession);
            setSessionData(getCheckoutSession);
            setIsLoadingSession(false);
        },
        onError: (e) => {
            console.error('Error fetching checkout session: ', e);
            setIsLoadingSession(false);
            setHasError(true);
        }
    });

    const [fetchBillingAccount] = useLazyQuery(graphqlSchema.PROFILE.getBillingAccount, {
        onCompleted: ({getBillingAccount}: { getBillingAccount: BillingAccount }) => {
            console.log('Fetched Billing Account after checkout: ', getBillingAccount);
            setBillingAccount(getBillingAccount);
            // Expects subscription to be active, but webhook delays might require multiple attempts
            if(getBillingAccount.hasStripeSubscription) {
                setIsLoadingBilling(false);
                recallCurrentUser();
            } else {
                setAttempts(prev => prev + 1);
            }
        },
        onError: (e) => {
            console.error('Error fetching billing account: ', e);
            setHasError(true);
            setIsLoadingBilling(false);
        },
        fetchPolicy: 'network-only'
    });

    useEffect(() => {
        return () => {
            if(retryTimer) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
        }
    }, [])

    useEffect(() => {
        if (sessionData) {
            fetchBillingAccount();
        }        
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionData]);

    useEffect(() => {
        if(!billingAccount) return;
        if(!billingAccount.hasStripeSubscription && attempts < 5) {
            // If subscription is not active yet, retry fetching billing account
            retryTimer = setTimeout(() => {
                fetchBillingAccount();
            }, 2000); // Retry every 2 seconds
        } 
        if (billingAccount.hasStripeSubscription) {
            if (retryTimer) clearTimeout(retryTimer);
            setIsLoadingBilling(false);
        }
        if (attempts >= 5) {
            // 5 Attempts maximum
            setHasError(true);
            setIsLoadingBilling(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billingAccount, attempts])

    const formatCurrency = (amountCents: number) => {
        return `$${(amountCents / 100).toFixed(2)} MXN`;
    };

    const statusLabel = (status: string) => {
        switch (status) {
            case 'complete': return 'Completado';
            case 'open': return 'Abierto';
            case 'expired': return 'Expirado';
            default: return status;
        }
    };

    const subscriptionStatusLabel = (status: string) => {
        switch (status) {
            case 'active': return 'Activa';
            case 'incomplete': return 'Incompleta';
            case 'past_due': return 'Pago vencido';
            default: return status;
        }
    };

    return (
        <div id="CheckoutSuccess">
            <SectionTitleBar currentUser={currentUser} title="Confirmación de pago" />
            <div className="checkout-content">
                <div className="card">
                    <h4>Sesión de pago</h4>
                    <Loading display={isLoadingSession} relativePos svgWidth="80pt" svgHeight="80pt" />
                    {!isLoadingSession && sessionData && (
                        <div className="session-details">
                            <p><span>Estado:</span> {statusLabel(sessionData.status)}</p>
                            <p><span>Monto pagado:</span> {formatCurrency(sessionData.amountCents)}</p>
                            <p><span>Identificador de sesión:</span> {sessionData.id}</p>
                        </div>
                    )}
                    {!isLoadingSession && !sessionData && hasError && (
                        <p className="error">No se pudo obtener la información de la sesión de pago.</p>
                    )}
                </div>
                <div className="card">
                    <h4>Cuenta de facturación</h4>
                    <Loading display={isLoadingBilling} relativePos svgWidth="80pt" svgHeight="80pt" />
                    {isLoadingBilling && (
                        <p className="loading-hint">Confirmando la activación de tu suscripción{attempts > 0 ? ` (intento ${attempts} de 5)` : ''}...</p>
                    )}
                    {!isLoadingBilling && billingAccount?.hasStripeSubscription && (
                        <div className="billing-details">
                            <p><span>Plan:</span> {billingAccount.stripeSubscriptionCfdiUsage.priceName}</p>
                            <p><span>Estado de suscripción:</span> {subscriptionStatusLabel(billingAccount.stripeSubscriptionStatus)}</p>
                            <p><span>Facturas emitidas en el periodo:</span> {billingAccount.stripeSubscriptionCfdiUsage.currentUsage} / {billingAccount.stripeSubscriptionCfdiUsage.maxUsage}</p>
                            <p><span>Inicio del periodo:</span> {new Date(billingAccount.stripeSubscriptionCfdiUsage.periodStartsAt).toLocaleDateString('es-MX', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'})}</p>
                            <p><span>Fin del periodo:</span> {new Date(billingAccount.stripeSubscriptionCfdiUsage.periodEndsAt).toLocaleDateString('es-MX', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'})}</p>
                        </div>
                    )}
                    {!isLoadingBilling && !billingAccount?.hasStripeSubscription && hasError && (
                        <p className="error">No se pudo confirmar la activación de la suscripción. Por favor verifica tu cuenta más tarde.</p>
                    )}
                </div>
                <NavLink to="/setup/pagos" className="back-link">Volver a mi cuenta</NavLink>
            </div>
        </div>
    )
}

export default CheckoutSuccess;